/**
 * Live Convex queries that start from the last result this device saw (architecture §12,
 * "Client data cache"). A page that rendered the same query seconds ago (in this SPA, or before a
 * reload) paints it at once and marks it `stale` until the live subscription answers.
 *
 * Results are kept in memory (instant within the SPA) and in localStorage under
 * `circles.cache.v1.<hash>` — in sessionStorage instead while the builder key is session-only
 * (`cacheStorageChoice`, §14.78), so cached builder results do not outlive a tab-only sign-in —
 * JSON only, within a byte budget with least-recently-used eviction.
 * Never cached: results with a field named like a secret (`SECRET_FIELD`). The whole cache is
 * dropped when a device token disappears or the builder key changes (leave, switch member, forget key).
 */
import { STORAGE_KEYS } from '@circles/convex';
import { useQueries, type RequestForQueries } from 'convex/react';
import { browserSessionStorage, cacheStorageChoice, readAccountKey } from './accountKeyStorage';
import { getFunctionName, type FunctionArgs, type FunctionReference, type FunctionReturnType } from 'convex/server';
import { useEffect, useMemo, useRef } from 'react';

export const CACHE_PREFIX = 'circles.cache.v1.';
const INDEX_KEY = `${CACHE_PREFIX}$index`;
export const CACHE_BUDGET = 1_000_000;

/** Field names whose values must never reach the cache, at any depth. */
export const SECRET_FIELD = /pinhash|pinsalt|devicetoken|accountkey|^vapid|subscription/i;

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

/** Who the cached results belong to: hashes of the device tokens and of the builder key. */
export type CacheOwner = { tokens: string[]; account: string | null };

type Index = { owner: CacheOwner; entries: Record<string, { at: number; size: number }> };

/** cyrb53: a fast 53-bit string hash, base36. Not cryptographic; keys only need to be distinct. */
export function hashKey(input: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/** JSON with sorted object keys, so `{ a, b }` and `{ b, a }` share a cache entry. */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
}

/** False for anything that is not plain JSON (bigint, bytes) or that carries a secret-looking field. */
export function isCacheable(value: unknown, depth = 0): boolean {
  if (depth > 32) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every((v) => isCacheable(v, depth + 1));
  if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (SECRET_FIELD.test(k)) return false;
      if (v !== undefined && !isCacheable(v, depth + 1)) return false;
    }
    return true;
  }
  return false;
}

function sameOwner(a: CacheOwner, b: CacheOwner): boolean {
  return a.account === b.account && a.tokens.every((t) => b.tokens.includes(t));
}

export class QueryCache {
  private memory = new Map<string, { value: unknown; json: string }>();
  private index: Index | null = null;
  private ownerSig: string | null = null;
  /** Bumped by every `clear()`, so callers know to write their live results again. */
  generation = 0;

  constructor(
    private storage: StorageLike | null,
    private opts: { budget?: number; owner: () => CacheOwner; now?: () => number },
  ) {}

  private get budget(): number {
    return this.opts.budget ?? CACHE_BUDGET;
  }

  private now(): number {
    return (this.opts.now ?? Date.now)();
  }

  private readIndex(): Index {
    if (this.index) return this.index;
    let parsed: Index | null = null;
    try {
      const raw = this.storage?.getItem(INDEX_KEY);
      parsed = raw ? (JSON.parse(raw) as Index) : null;
    } catch {
      parsed = null;
    }
    this.index = parsed && parsed.owner && parsed.entries ? parsed : { owner: this.opts.owner(), entries: {} };
    return this.index;
  }

  private writeIndex(): void {
    if (!this.storage || !this.index) return;
    try {
      this.storage.setItem(INDEX_KEY, JSON.stringify(this.index));
    } catch {
      // A full storage only costs the next page load its head start.
    }
  }

  /**
   * Drops everything when the device lost a token or the builder key changed since the
   * cache was written. A newly added token keeps the cache: nothing cached belongs to it yet.
   */
  private checkOwner(): void {
    const owner = this.opts.owner();
    const sig = `${owner.account}|${owner.tokens.join(',')}`;
    if (sig === this.ownerSig) return;
    this.ownerSig = sig;
    const index = this.readIndex();
    if (!sameOwner(index.owner, owner)) this.clear();
    const fresh = this.readIndex();
    fresh.owner = owner;
    this.writeIndex();
  }

  get(key: string): unknown {
    this.checkOwner();
    const hit = this.memory.get(key);
    if (hit) return hit.value;
    const index = this.readIndex();
    const meta = index.entries[key];
    if (!meta || !this.storage) return undefined;
    try {
      const raw = this.storage.getItem(CACHE_PREFIX + key);
      if (raw === null) throw new Error('missing');
      const value = (JSON.parse(raw) as { v: unknown }).v;
      this.memory.set(key, { value, json: raw });
      meta.at = this.now();
      this.writeIndex();
      return value;
    } catch {
      this.delete(key);
      return undefined;
    }
  }

  set(key: string, value: unknown, persist = true): void {
    this.checkOwner();
    if (value === undefined || !isCacheable(value)) {
      this.delete(key);
      return;
    }
    const json = JSON.stringify({ v: value });
    const prev = this.memory.get(key);
    this.memory.set(key, { value, json });
    if (!persist || !this.storage) return;
    const index = this.readIndex();
    const meta = index.entries[key];
    if (prev?.json === json && meta) {
      meta.at = this.now();
      return;
    }
    const size = json.length + key.length + CACHE_PREFIX.length;
    if (size > this.budget) {
      this.removeStored(key);
      this.writeIndex();
      return;
    }
    delete index.entries[key];
    this.evictUntil(this.budget - size);
    for (;;) {
      try {
        this.storage.setItem(CACHE_PREFIX + key, json);
        break;
      } catch {
        if (!this.evictOldest()) {
          this.removeStored(key);
          this.writeIndex();
          return;
        }
      }
    }
    index.entries[key] = { at: this.now(), size };
    this.writeIndex();
  }

  delete(key: string): void {
    this.memory.delete(key);
    if (this.removeStored(key)) this.writeIndex();
  }

  clear(): void {
    this.generation += 1;
    this.memory.clear();
    const index = this.readIndex();
    index.entries = {};
    if (this.storage) {
      const keys: string[] = [];
      for (let i = 0; i < this.storage.length; i++) {
        const k = this.storage.key(i);
        if (k?.startsWith(CACHE_PREFIX) && k !== INDEX_KEY) keys.push(k);
      }
      for (const k of keys) this.storage.removeItem(k);
    }
    this.writeIndex();
  }

  /** Keys of the persisted entries, least recently used first (tests and debugging). */
  storedKeys(): string[] {
    const entries = this.readIndex().entries;
    return Object.keys(entries).sort((a, b) => entries[a]!.at - entries[b]!.at);
  }

  storedBytes(): number {
    return Object.values(this.readIndex().entries).reduce((n, e) => n + e.size, 0);
  }

  private removeStored(key: string): boolean {
    const index = this.readIndex();
    const had = key in index.entries;
    delete index.entries[key];
    try {
      this.storage?.removeItem(CACHE_PREFIX + key);
    } catch {
      // Nothing to do: the entry is out of the index either way.
    }
    return had;
  }

  private evictOldest(): boolean {
    const oldest = this.storedKeys()[0];
    if (oldest === undefined) return false;
    this.removeStored(oldest);
    return true;
  }

  private evictUntil(limit: number): void {
    while (this.storedBytes() > limit && this.evictOldest()) {
      // evictOldest makes progress each turn.
    }
  }
}

function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function sessionBrowserStorage(): StorageLike | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** The storage the cache persists to right now: it follows the builder key (`cacheStorageChoice`, §14.78). */
function pickCacheStorage(): StorageLike | null {
  const local = browserStorage();
  const session = sessionBrowserStorage();
  return cacheStorageChoice(local, session) === 'session' ? session : local;
}

/**
 * A StorageLike that re-picks its backing storage on every call, so a session-only sign-in moves
 * the cache to `sessionStorage` without a reload (cached builder results must not outlive the tab)
 * and a kept or signed-out browser keeps using `localStorage`. `setItem` lets a quota error
 * through: `QueryCache.set` relies on it to evict.
 */
const switchingStorage: StorageLike = {
  getItem: (key) => pickCacheStorage()?.getItem(key) ?? null,
  setItem: (key, value) => pickCacheStorage()?.setItem(key, value),
  removeItem: (key) => pickCacheStorage()?.removeItem(key),
  key: (i) => pickCacheStorage()?.key(i) ?? null,
  get length() {
    return pickCacheStorage()?.length ?? 0;
  },
};

/** Hashes of every device token and of the builder key on this browser (never the values). */
function currentOwner(): CacheOwner {
  const storage = browserStorage();
  let tokens: string[] = [];
  try {
    const parsed = JSON.parse(storage?.getItem(STORAGE_KEYS.deviceTokens) ?? '{}') as Record<string, { token?: unknown }>;
    tokens = Object.values(parsed)
      .map((e) => (typeof e?.token === 'string' ? hashKey(e.token) : ''))
      .filter(Boolean)
      .sort();
  } catch {
    tokens = [];
  }
  const accountKey = readAccountKey(storage, browserSessionStorage());
  return { tokens, account: accountKey ? hashKey(accountKey) : null };
}

const cache = new QueryCache(switchingStorage, { owner: currentOwner });

/** Forgets every cached result, in memory and in localStorage. */
export function clearQueryCache(): void {
  cache.clear();
}

export function queryCacheKey(name: string, args: unknown): string {
  return hashKey(`${name}|${stableStringify(args)}`);
}

export type CachedQuery<T> = {
  data: T | undefined;
  error: Error | null;
  /** True while `data` is the cached result and the live one has not arrived yet. */
  stale: boolean;
};

export type CacheOptions = {
  /** Cache under this key instead of function name + args. */
  key?: string;
  /** Also keep the result in localStorage (default). `false` keeps it in memory only. */
  persist?: boolean;
};

/** One live query per key (e.g. per circle), each served from the cache until it answers. */
export function useCachedQueries<Q extends FunctionReference<'query'>>(
  query: Q,
  argsByKey: Record<string, FunctionArgs<Q>>,
  opts: CacheOptions = {},
): Record<string, CachedQuery<FunctionReturnType<Q>>> {
  // `api.x.y` is a fresh proxy on every access, so memoise on the name, not the reference.
  const name = getFunctionName(query);
  const argsKey = JSON.stringify(argsByKey);
  const request = useMemo(() => {
    const r: RequestForQueries = {};
    for (const [k, args] of Object.entries(argsByKey)) r[k] = { query, args };
    return r;
  }, [name, argsKey]);
  const keys = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, args] of Object.entries(argsByKey)) {
      out[k] = opts.key !== undefined ? hashKey(`${opts.key}|${k}`) : queryCacheKey(name, args);
    }
    return out;
  }, [name, argsKey, opts.key]);
  const values = useQueries(request) as Record<string, unknown>;
  const persist = opts.persist ?? true;

  const written = useRef(new Map<string, { value: unknown; generation: number }>());
  useEffect(() => {
    for (const [k, cacheKey] of Object.entries(keys)) {
      const v = values[k];
      const last = written.current.get(cacheKey);
      if (v === undefined || (last?.value === v && last.generation === cache.generation)) continue;
      if (v instanceof Error) cache.delete(cacheKey);
      else cache.set(cacheKey, v, persist);
      written.current.set(cacheKey, { value: v, generation: cache.generation });
    }
  }, [values, keys, persist]);

  const out: Record<string, CachedQuery<FunctionReturnType<Q>>> = {};
  for (const [k, cacheKey] of Object.entries(keys)) {
    const v = values[k];
    if (v instanceof Error) out[k] = { data: undefined, error: v, stale: false };
    else if (v !== undefined) out[k] = { data: v as FunctionReturnType<Q>, error: null, stale: false };
    else {
      const cached = cache.get(cacheKey) as FunctionReturnType<Q> | undefined;
      out[k] = { data: cached, error: null, stale: cached !== undefined };
    }
  }
  return out;
}

const SKIP: CachedQuery<never> = { data: undefined, error: null, stale: false };

/** `useQuery` that answers from the cache first; errors come back as a value instead of throwing. */
export function useCachedQuery<Q extends FunctionReference<'query'>>(
  query: Q,
  args: FunctionArgs<Q> | 'skip',
  opts: CacheOptions = {},
): CachedQuery<FunctionReturnType<Q>> {
  const argsByKey = useMemo(
    (): Record<string, FunctionArgs<Q>> => (args === 'skip' ? {} : { q: args }),
    [args === 'skip' ? 'skip' : JSON.stringify(args)],
  );
  return useCachedQueries(query, argsByKey, opts).q ?? SKIP;
}
